import type {
  Customer,
  DebtAgingBucket,
  DebtSummary,
  MembershipCharge,
  MembershipPlan,
  Purchase,
  ReceivableEntry,
  Sale,
  Supplier,
  SupplierPayableEntry,
} from '../../types/domain';

const dateOnly = (value: string) => value.slice(0, 10);
const addDays = (value: string, days: number) => {
  const date = new Date(`${dateOnly(value)}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};
const daysBetween = (from: string, to: string) => Math.floor((new Date(`${to}T12:00:00Z`).getTime() - new Date(`${from}T12:00:00Z`).getTime()) / 86400000);
const today = () => new Date().toISOString().slice(0, 10);
const bucket = (days: number): DebtAgingBucket => days <= 0 ? 'current' : days <= 30 ? '1-30' : days <= 60 ? '31-60' : days <= 90 ? '61-90' : '90+';
const status = (dueDate: string): 'current' | 'due-soon' | 'overdue' => {
  const days = daysBetween(today(), dueDate);
  if (days < 0) return 'overdue';
  return days <= 7 ? 'due-soon' : 'current';
};

export function buildReceivables(input: {
  sales: Sale[];
  customers: Customer[];
  membershipCharges: MembershipCharge[];
  membershipPlans: MembershipPlan[];
  defaultDueDays: number;
}): ReceivableEntry[] {
  const customers = new Map(input.customers.map((customer) => [customer.id, customer]));
  const saleRows = input.sales.filter((sale) => sale.customerId && sale.balanceCents > 0 && sale.status !== 'cancelled').map((sale): ReceivableEntry => {
    const customer = customers.get(sale.customerId!);
    const dueDate = sale.dueDate ?? (sale.mode === 'special-order' ? sale.promisedDate : undefined) ?? addDays(sale.createdAt, input.defaultDueDays);
    const overdueDays = Math.max(0, -daysBetween(today(), dueDate));
    const source = sale.mode === 'layaway' ? 'layaway' : sale.mode === 'special-order' ? 'special-order' : 'credit-sale';
    return {
      id: `sale:${sale.id}`, source, sourceId: sale.id, folio: sale.folio, businessId: sale.businessId, branchId: sale.branchId,
      customerId: sale.customerId!, customerName: customer?.name ?? 'Cliente eliminado', description: source === 'layaway' ? 'Apartado' : source === 'special-order' ? 'Pedido por encargo' : 'Venta a crédito',
      issuedDate: dateOnly(sale.createdAt), dueDate, totalCents: sale.totalCents, paidCents: sale.paidCents, balanceCents: sale.balanceCents, overdueDays,
      agingBucket: bucket(overdueDays), phone: customer?.phone ?? '', whatsapp: customer?.whatsapp ?? '', status: status(dueDate),
    };
  });
  const membershipRows = input.membershipCharges.filter((charge) => charge.status !== 'paid' && charge.status !== 'voided' && charge.amountCents > charge.paidCents).map((charge): ReceivableEntry => {
    const customer = customers.get(charge.customerId);
    const overdueDays = Math.max(0, -daysBetween(today(), charge.dueDate));
    return {
      id: `membership:${charge.id}`, source: 'membership', sourceId: charge.id, folio: charge.folio, businessId: charge.businessId,
      customerId: charge.customerId, customerName: customer?.name ?? 'Cliente eliminado', description: 'Cobro de membresía', issuedDate: dateOnly(charge.createdAt), dueDate: charge.dueDate,
      totalCents: charge.amountCents, paidCents: charge.paidCents, balanceCents: charge.amountCents - charge.paidCents, overdueDays, agingBucket: bucket(overdueDays),
      phone: customer?.phone ?? '', whatsapp: customer?.whatsapp ?? '', status: status(charge.dueDate),
    };
  });
  return [...saleRows, ...membershipRows].sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

export function buildSupplierPayables(input: { purchases: Purchase[]; suppliers: Supplier[]; defaultDueDays: number }): SupplierPayableEntry[] {
  const suppliers = new Map(input.suppliers.map((supplier) => [supplier.id, supplier]));
  return input.purchases.filter((purchase) => purchase.balanceCents > 0 && purchase.status !== 'draft' && purchase.status !== 'cancelled').map((purchase) => {
    const supplier = suppliers.get(purchase.supplierId);
    const dueDate = purchase.dueDate ?? addDays(purchase.purchasedAt, input.defaultDueDays);
    const overdueDays = Math.max(0, -daysBetween(today(), dueDate));
    return {
      id: `purchase:${purchase.id}`, purchaseId: purchase.id, folio: purchase.folio, businessId: purchase.businessId, branchId: purchase.branchId,
      supplierId: purchase.supplierId, supplierName: supplier?.name ?? 'Proveedor eliminado', description: purchase.invoiceNumber ? `Factura ${purchase.invoiceNumber}` : 'Compra a proveedor',
      issuedDate: purchase.purchasedAt, dueDate, totalCents: purchase.totalCents, paidCents: purchase.paidCents, balanceCents: purchase.balanceCents,
      overdueDays, agingBucket: bucket(overdueDays), status: status(dueDate),
    } satisfies SupplierPayableEntry;
  }).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
}

export function buildDebtSummary(receivables: ReceivableEntry[], payables: SupplierPayableEntry[]): DebtSummary {
  const aging: DebtSummary['aging'] = { current: 0, '1-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
  for (const debt of receivables) aging[debt.agingBucket] += debt.balanceCents;
  return {
    receivableCents: receivables.reduce((sum, debt) => sum + debt.balanceCents, 0),
    overdueReceivableCents: receivables.filter((debt) => debt.status === 'overdue').reduce((sum, debt) => sum + debt.balanceCents, 0),
    dueSoonCents: receivables.filter((debt) => debt.status === 'due-soon').reduce((sum, debt) => sum + debt.balanceCents, 0),
    payableCents: payables.reduce((sum, debt) => sum + debt.balanceCents, 0),
    overduePayableCents: payables.filter((debt) => debt.status === 'overdue').reduce((sum, debt) => sum + debt.balanceCents, 0),
    customersWithDebt: new Set(receivables.map((debt) => debt.customerId)).size,
    suppliersWithDebt: new Set(payables.map((debt) => debt.supplierId)).size,
    aging,
  };
}
